import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Clock,
  MapPin,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  MessageSquare,
  Loader2,
  FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/v2';

/**
 * MobileRUHDetail - Detaljvisning av RUH på mobil
 */
export default function MobileRUHDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const [ruh, setRuh] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && id) {
      fetchRUHDetail();
    }
  }, [user, id]);

  const fetchRUHDetail = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      
      // Fetch RUH details
      const ruhResponse = await axios.get(`${API}/ops/deviations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRuh(ruhResponse.data);
      
      // Fetch actions
      const actionsResponse = await axios.get(`${API}/ops/deviations/${id}/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActions(actionsResponse.data.actions || []);
    } catch (error) {
      console.error('Failed to fetch RUH detail:', error);
      toast.error(isNorwegian ? 'Kunne ikke laste RUH' : 'Could not load RUH');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'in_progress': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'closed': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertTriangle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'closed': return <CheckCircle2 className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: isNorwegian ? 'Åpen' : 'Open',
      in_progress: isNorwegian ? 'Under arbeid' : 'In progress',
      closed: isNorwegian ? 'Lukket' : 'Closed'
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      near_miss: isNorwegian ? 'Nestenulykke' : 'Near miss',
      personal_injury: isNorwegian ? 'Personskade' : 'Personal injury',
      material_damage: isNorwegian ? 'Materiell skade' : 'Material damage',
      dangerous_situation: isNorwegian ? 'Farlig situasjon' : 'Dangerous situation',
      environmental: isNorwegian ? 'Miljøhendelse' : 'Environmental',
      other: isNorwegian ? 'Annet' : 'Other'
    };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(isNorwegian ? 'nb-NO' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    navigate('/m/ruh/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  if (!ruh) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8">
        <AlertTriangle className="w-12 h-12 text-slate-600 mb-4" />
        <p className="text-slate-400 text-center">
          {isNorwegian ? 'RUH ikke funnet' : 'RUH not found'}
        </p>
        <button
          onClick={() => navigate('/m/ruh')}
          className="mt-4 px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg font-medium"
        >
          {isNorwegian ? 'Tilbake' : 'Back'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="mobile-ruh-detail">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900 border-b border-slate-800 safe-area-top">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/m/ruh')}
              className="p-2 -ml-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-white truncate">
                {ruh.title || 'RUH'}
              </h1>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(ruh.status)}`}>
                {getStatusIcon(ruh.status)}
                {getStatusLabel(ruh.status)}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pb-8">
        <div className="p-4 space-y-4">
          {/* Category badge */}
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300">
              {getCategoryLabel(ruh.ruh_category)}
            </span>
          </div>

          {/* Description */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-2">
              {isNorwegian ? 'Beskrivelse' : 'Description'}
            </h3>
            <p className="text-white whitespace-pre-wrap">
              {ruh.description}
            </p>
          </div>

          {/* Meta info */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">
            {ruh.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">{isNorwegian ? 'Sted' : 'Location'}</p>
                  <p className="text-white">{ruh.location}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">{isNorwegian ? 'Registrert' : 'Registered'}</p>
                <p className="text-white">{formatDate(ruh.created_at)}</p>
              </div>
            </div>

            {ruh.incident_datetime && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">{isNorwegian ? 'Hendelsestidspunkt' : 'Incident time'}</p>
                  <p className="text-white">{formatDate(ruh.incident_datetime)}</p>
                </div>
              </div>
            )}

            {ruh.responsible && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">{isNorwegian ? 'Ansvarlig' : 'Responsible'}</p>
                  <p className="text-white">{ruh.responsible}</p>
                </div>
              </div>
            )}
          </div>

          {/* Immediate actions */}
          {ruh.immediate_actions && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-slate-400 mb-2">
                {isNorwegian ? 'Strakstiltak' : 'Immediate actions'}
              </h3>
              <p className="text-white whitespace-pre-wrap">
                {ruh.immediate_actions}
              </p>
            </div>
          )}

          {/* Actions/Tiltak */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {isNorwegian ? 'Tiltak' : 'Actions'} ({actions.length})
            </h3>
            
            {actions.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">
                {isNorwegian ? 'Ingen tiltak registrert' : 'No actions registered'}
              </p>
            ) : (
              <div className="space-y-3">
                {actions.map((action) => (
                  <div 
                    key={action.id}
                    className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-white font-medium">{action.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        action.status === 'completed' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {action.status === 'completed' 
                          ? (isNorwegian ? 'Ferdig' : 'Done')
                          : (isNorwegian ? 'Åpen' : 'Open')
                        }
                      </span>
                    </div>
                    {action.description && (
                      <p className="text-sm text-slate-400 mt-1">{action.description}</p>
                    )}
                    {action.responsible && (
                      <p className="text-xs text-slate-500 mt-2">
                        {isNorwegian ? 'Ansvarlig:' : 'Responsible:'} {action.responsible}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Escalation notice */}
          {ruh.escalated_to_deviation_id && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">
                  {isNorwegian ? 'Eskalert til avvik' : 'Escalated to deviation'}
                </span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
